
import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { updateServiceImage } from '@/data/services';
import CalendarSettingsTab from '@/components/admin/calendar/CalendarSettingsTab';
import { ServiceFormFields } from './ServiceFormFields';
import { useAdminCheckQuery } from '@/hooks/useAdminCheckQuery';
import { useDebounce } from '@/hooks/useDebounce';
import { Service } from '@/types/service';

interface AdminServiceTabsContentProps {
  serviceId?: string;
  isAuthorized: boolean;
  handleSaveAndReturn?: () => void;
  isAddMode?: boolean;
}

const AdminServiceTabsContent: React.FC<AdminServiceTabsContentProps> = ({
  serviceId,
  isAuthorized,
  handleSaveAndReturn,
  isAddMode = false
}) => {
  const [activeTab, setActiveTab] = useState("general");
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({});
  const [serviceData, setServiceData] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const { data: isAdmin } = useAdminCheckQuery();
  
  // Add debounced form data for better performance
  const debouncedFormData = useDebounce(formData, 300);

  // Load users and regions (needed for both tabs)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const { data: userData, error: userError } = await supabase
          .from('profiles')
          .select('id, display_name, email');
        
        if (userError) throw userError;
        setUsers(userData || []);

        // Fetch regions
        const { data: regionData, error: regionError } = await supabase
          .from('regions')
          .select('*')
          .eq('active', true)
          .order('name');
        
        if (regionError) throw regionError;
        setRegions(regionData || []);
      } catch (error) {
        console.error('Error fetching form dependencies:', error);
      }
    };

    fetchData();
  }, []);

  // Fetch service data if editing
  useEffect(() => {
    const fetchService = async () => {
      if (serviceId && !isAddMode) {
        try {
          setDataLoading(true);
          console.log(`Fetching service with ID: ${serviceId}`);
          
          // Use a simplified query structure
          const { data, error } = await supabase
            .from('services')
            .select(`
              *,
              images:service_images(
                id,
                service_id,
                is_index_image,
                is_details_image,
                display_order,
                bucket,
                original_path
              )
            `)
            .eq('id', parseInt(serviceId))
            .single();
          
          if (error) {
            console.error('Error fetching service:', error);
            throw error;
          }
          
          if (data) {
            console.log('Service data fetched:', data);
            setServiceData(data);
            
            // Initialize form data with service data
            const initialFormData: Partial<Service> = {
              name: data.name || "",
              description: data.description || "",
              price: data.price || "",
              location: data.location || "",
              email: data.email || "",
              phone: data.phone || "",
              subcategory: data.subcategory || null,
              owner_id: data.owner_id || null,
              published: data.published || false,
              calendar: data.calendar || false,
              region: data.region || "nairobi",
              vendor_can_edit: data.vendor_can_edit || false,
              test_only: data.test_only || false,
              featured: data.featured || false,
              display_order: data.display_order || null,
              slot_size: data.slot_size || "30"
            };
            
            // Handle image URL - prioritize direct image_url first for backward compatibility
            if (data.image_url) {
              initialFormData.image_url = data.image_url;
            } 
            // Then check for images array as a fallback
            else if (data.images && data.images.length > 0) {
              // Find index image or use first image
              const indexImage = data.images.find((img: any) => img.is_index_image);
              const firstImage = data.images[0];
              const imageToUse = indexImage || firstImage;
              
              if (imageToUse?.original_path) {
                const { data: urlData } = supabase.storage
                  .from('services')
                  .getPublicUrl(imageToUse.original_path);
                
                initialFormData.image_url = urlData.publicUrl;
              }
            }
            
            setFormData(initialFormData);
          }
        } catch (error) {
          console.error('Error fetching service:', error);
          toast({
            title: "Error",
            description: "Failed to load service details",
            variant: "destructive",
          });
        } finally {
          setDataLoading(false);
        }
      } else {
        // For add mode, just initialize empty form
        setFormData({
          region: "nairobi",
          published: false,
          calendar: false
        });
        setDataLoading(false);
      }
    };

    fetchService();
  }, [serviceId, toast, isAddMode]);

  // Handle form data changes from child component
  const handleFormDataChange = (data: Partial<Service>) => {
    setFormData(data);
  };

  // Handle image changes from child component
  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
  };

  // Save service data
  const handleSave = async () => {
    if (!isAuthorized) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to update this service",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const serviceData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price.toString()) : null,
        subcategory: formData.subcategory || null,
        owner_id: formData.owner_id === "none" || !formData.owner_id ? null : formData.owner_id,
        display_order: formData.display_order === null ? null : formData.display_order,
        // Set slot_size to null if calendar is false, or use the specified value / default
        slot_size: formData.calendar ? (formData.slot_size || "30") : null
      };

      console.log("Saving service data:", serviceData);

      let result;
      
      if (isAddMode) {
        const { data: insertData, error: insertError } = await supabase
          .from('services')
          .insert(serviceData)
          .select()
          .single();

        if (insertError) throw insertError;
        result = insertData;
        console.log("Service created:", result);
      } else {
        const { data: updateData, error: updateError } = await supabase
          .from('services')
          .update(serviceData)
          .eq('id', serviceId ? parseInt(serviceId) : 0)
          .select()
          .single();

        if (updateError) throw updateError;
        result = updateData;
        console.log("Service updated:", result);
      }

      // Handle image upload if an image was selected
      if (selectedImage && result) {
        try {
          console.log("Uploading image for service:", result.id);
          await updateServiceImage(result.id, selectedImage);
          console.log("Image upload completed successfully");
        } catch (imageError: any) {
          console.error('Error saving image:', imageError);
          // Show error but don't fail the whole operation
          toast({
            title: "Image Upload Warning",
            description: "Service data was saved but there was an issue uploading the image: " + imageError.message,
            variant: "warning",
          });
        }
      }

      toast({
        title: "Success",
        description: isAddMode ? "Service created successfully" : "Service updated successfully",
        variant: "success",
      });

      if (handleSaveAndReturn) {
        handleSaveAndReturn();
      }
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: "Error",
        description: "Failed to save service: " + (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const hasCalendar = formData?.calendar;

  return (
    <div>
      <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="admin" disabled={!serviceId && isAddMode}>Admin Settings</TabsTrigger>
          <TabsTrigger value="calendar" disabled={isAddMode || !serviceId || !hasCalendar}>Calendar Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          {dataLoading ? (
            <div className="p-8 text-center">Loading service details...</div>
          ) : (
            <>
              <ServiceFormFields
                initialData={formData}
                users={users}
                regions={regions}
                isAdmin={false}
                isAuthorized={isAuthorized}
                isAddMode={isAddMode}
                loading={loading}
                onFormChange={handleFormDataChange}
                onImageChange={handleImageChange}
              />
              
              <div className="flex justify-end gap-4 mt-4">
                <Button onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : (isAddMode ? "Create Service" : "Save Changes")}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="admin" className="space-y-4">
          {dataLoading ? (
            <div className="p-8 text-center">Loading service details...</div>
          ) : (
            <>
              <ServiceFormFields
                initialData={formData}
                users={users}
                regions={regions}
                isAdmin={true}
                isAuthorized={isAuthorized}
                isAddMode={isAddMode}
                loading={loading}
                onFormChange={handleFormDataChange}
              />
              
              <div className="flex justify-end gap-4 mt-4">
                <Button onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save Admin Settings"}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4">
          {serviceId && hasCalendar && (
            <CalendarSettingsTab serviceId={serviceId} />
          )}
          {!hasCalendar && (
            <div className="p-4 text-center text-muted-foreground">
              Calendar features are disabled for this service. Enable them in Admin Settings.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminServiceTabsContent;
